import {
    CombinedFaceTracker,
    type FaceTrackerStateChangedArg,
    type TrackedFace,
    type Track,
    TrackState,
    type BoundingBox,
    FaceTrackedSubjectState,
    getFrontalityScore,
} from 'econnect-face-rec-web';
import { observable, action, observe, ObservableMap, computed, runInAction } from 'mobx';
import { DisposableBase } from '../Types/index.ts';
import { TimeAverage } from '../Utils/TimeAverage.ts';
import { AnimationLoop } from '../Utils/AnimationLoop.ts';
import { FaceSdkStore } from './FaceSdkStore.tsx';
import { MainStore } from './MainStore.ts';
import { Api, type FaceEdgeRecognizeWithOptions, type FaceEdgePublishResponse } from '../Clients/Identities/Api.ts';
import { debugLog } from './DebugLogStore.ts';
import { bitmapToBase64Jpeg } from '../Utils/imageEncoding.ts';
import { awaitClassification } from './awaitClassification.ts';

// ── Types ──

export const FaceDetectionStatus = {
    None: 'None',
    ProcessingFace: 'ProcessingFace',
    FaceTooSmall: 'FaceTooSmall',
    FaceQualityTooLow: 'FaceQualityTooLow',
} as const;

export type FaceDetectionStatus = (typeof FaceDetectionStatus)[keyof typeof FaceDetectionStatus];
export type FaceDetectionStatusType = 'processing' | 'warning' | 'error' | null;

type ImageSource = HTMLVideoElement | HTMLCanvasElement | ImageBitmap | OffscreenCanvas;

interface BestFaceCapture {
    expandedImage: ImageBitmap;
    quality: number;
    capturedAt: number;
    submitted: boolean;
}

type FaceIssue = 'tooSmall' | 'lowQuality' | 'lowSharpness' | null;

/** Strategy for what happens when a best-face capture is ready to submit. */
export type CaptureSubmissionHandler = (image: ImageBitmap, subjectId: string) => Promise<void>;

// ── Constants ──

const STATUS_TYPE_MAP: Record<FaceDetectionStatus, FaceDetectionStatusType> = {
    [FaceDetectionStatus.ProcessingFace]: 'processing',
    [FaceDetectionStatus.FaceTooSmall]: 'warning',
    [FaceDetectionStatus.FaceQualityTooLow]: 'error',
    [FaceDetectionStatus.None]: null,
};

const CLASSIFICATION_TIMEOUT_MS = 3000;
const QUALITY_IMPROVEMENT_THRESHOLD = 0.04;

// ── Helpers ──

function getImageDimensions(image: ImageSource): { width: number; height: number } {
    if (image instanceof HTMLVideoElement) return { width: image.videoWidth, height: image.videoHeight };
    return { width: image.width, height: image.height };
}

function cropFaceRegion(bbox: BoundingBox, expansionFactor: number, source: ImageBitmap): ImageBitmap {
    const cx = bbox.origin.x + bbox.width / 2;
    const cy = bbox.origin.y + bbox.height / 2;
    const w = bbox.width * expansionFactor;
    const h = bbox.height * expansionFactor;
    const x = Math.max(0, cx - w / 2);
    const y = Math.max(0, cy - h / 2);
    const cw = Math.min(w, source.width - x);
    const ch = Math.min(h, source.height - y);

    const canvas = new OffscreenCanvas(cw, ch);
    canvas.getContext('2d')!.drawImage(source, x, y, cw, ch, 0, 0, cw, ch);
    return canvas.transferToImageBitmap();
}

function closeBitmap(bitmap: ImageBitmap | null | undefined) {
    if (!bitmap) return;
    try {
        bitmap.close();
    } catch {
        // Already closed or detached; nothing else to release.
    }
}

function closeTrackedFaces(faces: readonly TrackedFace[]) {
    const bitmaps = new Set<ImageBitmap>();
    for (const face of faces) {
        const faceResources = face as TrackedFace & {
            preparedImage?: { input?: ImageBitmap };
            aligned?: ImageBitmap;
        };
        if (face.sourceImage) bitmaps.add(face.sourceImage);
        if (faceResources.preparedImage?.input) bitmaps.add(faceResources.preparedImage.input);
        if (faceResources.aligned) bitmaps.add(faceResources.aligned);
    }
    for (const bitmap of bitmaps) {
        closeBitmap(bitmap);
    }
}

function closeCapture(capture: BestFaceCapture | null | undefined) {
    closeBitmap(capture?.expandedImage);
}

// Reusable canvas + buffer for Laplacian variance — avoids per-frame allocation.
// Max bound on the longer dimension of the resampled patch; the shorter side
// scales proportionally to preserve the source bbox's aspect ratio.
const LAPLACIAN_MAX_SIZE = 128;
let _laplacianCanvas: OffscreenCanvas | null = null;
let _laplacianCtx: OffscreenCanvasRenderingContext2D | null = null;
let _laplacianGray: Float32Array | null = null;

/**
 * Variance of Laplacian as a no-reference sharpness/blurriness metric.
 * Crops `source` to `bbox` and resamples to fit within LAPLACIAN_MAX_SIZE on
 * the longer side, preserving aspect ratio. Converts to luma, runs a 3×3
 * Laplacian, returns the variance. Higher = sharper.
 */
function computeLaplacianVariance(source: ImageBitmap, bbox: BoundingBox): number {
    const x = Math.max(0, bbox.origin.x);
    const y = Math.max(0, bbox.origin.y);
    const srcW = Math.min(bbox.width, source.width - x);
    const srcH = Math.min(bbox.height, source.height - y);
    if (srcW <= 0 || srcH <= 0) return 0;

    const scale = Math.min(LAPLACIAN_MAX_SIZE / srcW, LAPLACIAN_MAX_SIZE / srcH, 1);
    const w = Math.max(3, Math.floor(srcW * scale));
    const h = Math.max(3, Math.floor(srcH * scale));

    if (!_laplacianCanvas || !_laplacianCtx) {
        _laplacianCanvas = new OffscreenCanvas(LAPLACIAN_MAX_SIZE, LAPLACIAN_MAX_SIZE);
        const ctx = _laplacianCanvas.getContext('2d', { willReadFrequently: true });
        if (!ctx) return 0;
        ctx.imageSmoothingEnabled = true;
        ctx.imageSmoothingQuality = 'high';
        _laplacianCtx = ctx;
    }
    const ctx = _laplacianCtx;

    ctx.clearRect(0, 0, w, h);
    ctx.drawImage(source, x, y, srcW, srcH, 0, 0, w, h);
    const { data } = ctx.getImageData(0, 0, w, h);

    const numPixels = w * h;
    if (!_laplacianGray || _laplacianGray.length < numPixels) {
        _laplacianGray = new Float32Array(LAPLACIAN_MAX_SIZE * LAPLACIAN_MAX_SIZE);
    }
    const gray = _laplacianGray;
    for (let i = 0, j = 0; i < data.length; i += 4, j++) {
        gray[j] = 0.299 * data[i] + 0.587 * data[i + 1] + 0.114 * data[i + 2];
    }

    let sum = 0;
    let sumSq = 0;
    let count = 0;
    for (let yy = 1; yy < h - 1; yy++) {
        for (let xx = 1; xx < w - 1; xx++) {
            const i = yy * w + xx;
            const lap =
                -gray[i - w - 1] - gray[i - w] - gray[i - w + 1] -
                gray[i - 1] + 8 * gray[i] - gray[i + 1] -
                gray[i + w - 1] - gray[i + w] - gray[i + w + 1];
            sum += lap;
            sumSq += lap * lap;
            count++;
        }
    }
    const mean = sum / count;
    return sumSq / count - mean * mean;
}

// ── Store ──

export class FaceServiceStore extends DisposableBase {
    private readonly _animationLoop = new AnimationLoop();
    private readonly _processTimeAvg = new TimeAverage(2);
    private readonly _tracker: CombinedFaceTracker;
    private readonly _faceSdkStore: FaceSdkStore;
    private readonly _rootStore: MainStore;

    // Per-subject timers for submission debounce and post-submit cleanup
    private readonly _submitTimers = new Map<string, ReturnType<typeof setTimeout>>();
    private readonly _cleanupTimers = new Map<string, ReturnType<typeof setTimeout>>();
    private readonly _activeSubmissions = new Set<string>();
    private _classificationResetTimer: ReturnType<typeof setTimeout> | null = null;
    private _previousTrackedFaceId: string | null = null;

    // Cached API client — recreated only when server address changes
    private _api: Api<unknown> | null = null;
    private _apiBaseUrl: string | null = null;

    constructor(faceSdkStore: FaceSdkStore, rootStore: MainStore) {
        super();
        this._faceSdkStore = faceSdkStore;
        this._rootStore = rootStore;

        this._tracker = new CombinedFaceTracker({
            embeddingTimeoutMs: 8000,
            boxTrackOptions: { iouThreshold: 0.1 },
        });
        this._tracker.addSubjectStateChangedListener(this._onSubjectStateChanged);
        this._tracker.addBoxTrackerStateChangedListener(this._onBoxTrackStateChanged);
        this._tracker.faceSdk = faceSdkStore.faceSdk;

        this._disposers.push(
            observe(faceSdkStore, 'faceSdk', (change) => {
                this._tracker.faceSdk = change.newValue;
            }),
            () => this._animationLoop.dispose(),
        );
    }

    private get _settings() {
        return this._rootStore.settingsStore;
    }

    // ── Tracker event handlers ──

    private _onSubjectStateChanged = (_: CombinedFaceTracker, args: FaceTrackerStateChangedArg) => {
        debugLog.info(`Subject state: ${args.subject.id} ${args.oldState} → ${args.newState}`);
        if (args.newState === FaceTrackedSubjectState.Expired || args.newState === FaceTrackedSubjectState.TimedOut) {
            const id = args.subject.id;
            this._deleteHighestQualityFace(id);
            this._clearTimer(this._submitTimers, id);
            this._clearTimer(this._cleanupTimers, id);
        }
    };

    private _onBoxTrackStateChanged = (_: CombinedFaceTracker, track: Track) => {
        if (track.state === TrackState.Dead) {
            this.laplacianByBoxId.delete(track.id);
            debugLog.info(`Box track ${track.id} dead`);
        }
    };

    // ── Observables ──

    @observable accessor error: string | null = null;
    @observable accessor videoElement: HTMLVideoElement | undefined | null = undefined;
    @observable accessor avgProcessImageTimeMs = 0;
    @observable accessor isDetecting = false;
    @observable accessor lastTrackedFaceId: string | null = null;
    @observable accessor lastFaceDetectionTime = 0;
    @observable accessor isProcessingFace = false;
    @observable accessor processingFaceImage: ImageBitmap | null = null;
    @observable accessor lastEventClassification: string | null = null;
    @observable accessor isEventOverlayActive = false;
    @observable accessor faces: TrackedFace[] = [];

    // Single observable drives both currentFaceTooSmall and currentFaceQualityTooLow via computeds
    @observable private accessor _faceIssue: FaceIssue = null;

    readonly highestQualityFaces = new ObservableMap<string, BestFaceCapture>();

    /** Debug: Laplacian-variance sharpness per box-tracker id. */
    readonly laplacianByBoxId = new Map<number, number>();

    /** Submission strategy. Defaults to the kiosk publish→classify flow. */
    submissionHandler: CaptureSubmissionHandler = (image) => this._kioskSubmit(image);

    private readonly _laplacianTimeAvg = new TimeAverage(2);
    private _lastLaplacianLogAt = 0;

    // ── Computed ──

    @computed get currentFaceTooSmall(): boolean {
        return this._faceIssue === 'tooSmall';
    }

    @computed get currentFaceQualityTooLow(): boolean {
        return this._faceIssue === 'lowQuality' || this._faceIssue === 'lowSharpness';
    }

    @computed get currentFaceSharpnessTooLow(): boolean {
        return this._faceIssue === 'lowSharpness';
    }

    @computed get status(): FaceDetectionStatus {
        if (this.isProcessingFace) return FaceDetectionStatus.ProcessingFace;
        if (this._faceIssue === 'tooSmall') return FaceDetectionStatus.FaceTooSmall;
        if (this._faceIssue === 'lowQuality' || this._faceIssue === 'lowSharpness') return FaceDetectionStatus.FaceQualityTooLow;
        return FaceDetectionStatus.None;
    }

    @computed get hasActiveStatus(): boolean {
        return this.status !== FaceDetectionStatus.None;
    }

    @computed get statusType(): FaceDetectionStatusType {
        return STATUS_TYPE_MAP[this.status];
    }

    // ── Public actions ──

    @action setError = (v: string | null) => { this.error = v; };

    @action setVideoElement = (video: HTMLVideoElement | undefined | null) => {
        if (this.videoElement === video) return;
        this.videoElement = video;

        // Restart detection if video element changed mid-detection
        if (this.isDetecting && video && video.readyState >= 3) {
            this.stopFaceDetection();
            queueMicrotask(() => this.autoStartDetection());
        }
    };

    @action setLastEventClassification = (v: string | null) => {
        this.lastEventClassification = v;
        if (v !== null) this._scheduleClassificationReset();
    };

    @action resetEventClassification = () => {
        this.lastEventClassification = null;
        this._clearClassificationTimer();
    };

    @action setEventOverlayActive = (active: boolean) => {
        this.isEventOverlayActive = active;

        // When the event overlay dismisses, reset stale face state so the
        // quality progress overlay doesn't briefly flash the pre-event values.
        if (!active) {
            this._clearFaceState();
            this._clearHighestQualityFaces();
        }
    };

    // ── Detection control ──

    toggleFaceDetection() {
        if (this.isDetecting) {
            this.stopFaceDetection();
        } else {
            this.startFaceDetection();
        }
    }

    @action startFaceDetection() {
        if (this.isDetecting || this._animationLoop.isRunning) return;

        if (!this._faceSdkStore.faceSdk || !this.videoElement) {
            this.error = !this._faceSdkStore.faceSdk
                ? 'FaceSDK not initialized.'
                : 'Video element not available for detection.';
            return;
        }

        this.isDetecting = true;
        this._animationLoop.start(this._detectionFrame);
    }

    @action autoStartDetection(): boolean {
        const canStart =
            !!this.videoElement &&
            !!this._faceSdkStore.faceSdk &&
            !this._faceSdkStore.sdkInitializing &&
            this.videoElement.readyState >= 3 &&
            !this.isDetecting &&
            !this._animationLoop.isRunning;

        if (canStart) this.startFaceDetection();
        return canStart;
    }

    @action stopFaceDetection() {
        this._animationLoop.stop();
        this.isDetecting = false;
        this._replaceFaces([]);
    }

    // ── Detection loop ──

    private _detectionFrame = async () => {
        const v = this.videoElement;
        if (!this.isDetecting || this.isEventOverlayActive || this.isProcessingFace || !v || v.paused || v.ended || v.readyState < 3 || !this._faceSdkStore.faceSdk) return;
        await this._processFrame(v);
    };

    /**
     * Core per-frame pipeline:
     *  1. Run combined tracker (detection + box tracking + quality — embeddings are throttled internally)
     *  2. Evaluate the primary face against size/quality thresholds
     *  3. If it qualifies, consider it for best-face capture (may trigger getLandmarks for frontality)
     *  4. Update observable face list for UI rendering
     */
    private async _processFrame(image: ImageSource) {
        if (!this._faceSdkStore.faceSdk) return;

        const t0 = performance.now();
        let detectedFaces: TrackedFace[] | null = null;
        try {
            const faces = await this._tracker.processImage(image, true);
            detectedFaces = faces;
            if (!this.isDetecting || this.isEventOverlayActive) {
                closeTrackedFaces(faces);
                detectedFaces = null;
                return;
            }

            // Compute Laplacian when the gate is in use OR when the debug overlay/log is on.
            const needLaplacian = this._settings.minLaplacian > 0 || this._settings.showNerdStatistics;
            let primaryLaplacian: number | null = null;
            if (needLaplacian) {
                for (const f of faces) {
                    if (f.boxTrackerId !== undefined) {
                        const lapStart = performance.now();
                        const lap = computeLaplacianVariance(f.sourceImage, f.boundingBox);
                        this._laplacianTimeAvg.add(performance.now() - lapStart);
                        this.laplacianByBoxId.set(f.boxTrackerId, lap);
                        if (f === faces[0]) primaryLaplacian = lap;
                    }
                }
                if (this._settings.showNerdStatistics) {
                    const now = performance.now();
                    if (now - this._lastLaplacianLogAt >= 1000) {
                        this._lastLaplacianLogAt = now;
                        const values = Array.from(this.laplacianByBoxId.entries())
                            .map(([bid, v]) => `BID ${bid}=${v.toFixed(1)}`)
                            .join(', ') || 'none';
                        debugLog.info(`Laplacian ${values} (avg compute: ${this._laplacianTimeAvg.getAverage().toFixed(2)}ms over 2s)`);
                    }
                }
            }

            if (faces.length > 0) {
                const { width, height } = getImageDimensions(image);
                const qualifies = this._evaluateFace(faces[0], width, height, primaryLaplacian);
                if (qualifies) await this._considerBestFace(faces[0]);
            } else {
                this._clearFaceState();
            }

            runInAction(() => { this._replaceFaces(faces); });
            detectedFaces = null;
        } catch (e) {
            if (detectedFaces) closeTrackedFaces(detectedFaces);
            console.error('Face detection error:', e);
        } finally {
            this._processTimeAvg.add(performance.now() - t0);
            runInAction(() => { this.avgProcessImageTimeMs = this._processTimeAvg.getAverage(); });
        }
    }

    // ── Face evaluation ──

    /** Returns true if the primary face meets minimum size, quality, and sharpness thresholds. */
    @action private _evaluateFace(face: TrackedFace, imgW: number, imgH: number, laplacian: number | null): boolean {
        const areaPct = ((face.boundingBox.width * face.boundingBox.height) / (imgW * imgH)) * 100;
        const tooSmall = areaPct < this._settings.minFaceSizePercent;
        const lowQuality = (face.quality ?? 0) < this._settings.minFaceQuality;
        const minLaplacian = this._settings.minLaplacian;
        const lowSharpness = minLaplacian > 0 && (laplacian ?? 0) < minLaplacian;

        this._faceIssue = tooSmall ? 'tooSmall' : lowQuality ? 'lowQuality' : lowSharpness ? 'lowSharpness' : null;

        const faceId = face.faceSubjectId ?? null;
        if (faceId !== this._previousTrackedFaceId) {
            this.lastFaceDetectionTime = performance.now();
        }
        this.lastTrackedFaceId = faceId;
        this._previousTrackedFaceId = faceId;

        return !tooSmall && !lowQuality && !lowSharpness;
    }

    @action private _clearFaceState() {
        this.lastTrackedFaceId = null;
        this._faceIssue = null;
        this._previousTrackedFaceId = null;
        this.laplacianByBoxId.clear();
    }

    @action private _replaceFaces(faces: TrackedFace[]) {
        const previousFaces = this.faces;
        this.faces = faces;
        if (previousFaces !== faces) {
            closeTrackedFaces(previousFaces);
        }
    }

    // ── Best face tracking ──

    /**
     * Tracks the highest-quality capture per subject. Key optimizations:
     *  - Skips if quality hasn't improved or subject was already submitted
     *  - Reuses landmarks from CombinedFaceTracker when available (avoids redundant expensive SDK call)
     *  - Uses timer-based submission instead of per-frame polling
     */
    private async _considerBestFace(face: TrackedFace) {
        const id = face.faceSubjectId;
        if (!id) return;

        // Skip if this subject already has an in-flight submission
        if (this._activeSubmissions.has(id)) return;

        const existing = this.highestQualityFaces.get(id);
        const quality = face.quality ?? 0;

        // Skip if already submitted or quality hasn't improved meaningfully
        if (existing && (existing.submitted || quality < existing.quality + QUALITY_IMPROVEMENT_THRESHOLD)) return;

        // Frontality gate — only call getLandmarks if we don't already have them.
        // CombinedFaceTracker.processImage() computes landmarks for faces that get embeddings,
        // so this avoids a redundant expensive SDK call in most cases.
        const { minFrontality } = this._settings;
        if (minFrontality > 0) {
            if (!face.landmarks) {
                await this._faceSdkStore.faceSdk!.getLandmarks([face]);
            }
            const frontality = face.landmarks ? getFrontalityScore(face.landmarks) : 0;
            if (frontality < minFrontality) {
                debugLog.info(`Face skipped: frontality ${frontality.toFixed(3)} < min ${minFrontality}`);
                return;
            }
        }

        debugLog.info(`Best face for ${id}: quality ${quality.toFixed(3)} (was ${existing?.quality.toFixed(3) ?? 'none'})`);

        const capture: BestFaceCapture = {
            expandedImage: cropFaceRegion(face.boundingBox, this._settings.faceExpansionFactor, face.sourceImage),
            quality,
            capturedAt: performance.now(),
            submitted: false,
        };
        const previousCapture = existing;
        this.highestQualityFaces.set(id, capture);
        closeCapture(previousCapture);

        // Schedule or reschedule submission (resets debounce on quality improvement)
        this._scheduleSubmission(id, capture);
    }

    private _deleteHighestQualityFace(id: string) {
        const capture = this.highestQualityFaces.get(id);
        if (!capture) return;

        this.highestQualityFaces.delete(id);
        if (!this._activeSubmissions.has(id) && this.processingFaceImage !== capture.expandedImage) {
            closeCapture(capture);
        }
    }

    private _clearHighestQualityFaces() {
        for (const id of Array.from(this.highestQualityFaces.keys())) {
            this._deleteHighestQualityFace(id);
        }
    }

    // ── Submission scheduling (timer-based instead of per-frame polling) ──

    /**
     * Debounced submission: waits maxFaceCaptureDurationMs for quality to stabilize.
     * If quality already meets the ideal threshold, submits immediately.
     */
    private _scheduleSubmission(id: string, capture: BestFaceCapture) {
        this._clearTimer(this._submitTimers, id);

        // Immediate submit if quality meets ideal threshold
        if (capture.quality >= this._settings.faceIdealQuality) {
            void this._submitCapture(id, capture);
            return;
        }

        // Otherwise debounce — each quality improvement reschedules
        const timer = setTimeout(() => {
            this._submitTimers.delete(id);
            const current = this.highestQualityFaces.get(id);
            if (current && !current.submitted) {
                void this._submitCapture(id, current);
            }
        }, this._settings.maxFaceCaptureDurationMs);
        this._submitTimers.set(id, timer);
    }

    /** The original kiosk behavior: publish to the configured detector and await classification. */
    private async _kioskSubmit(image: ImageBitmap): Promise<void> {
        const response = await this._publishFace(image);
        const detectId = response?.detectedFaces?.[0]?.detectId;
        if (detectId) {
            await this._awaitClassification(detectId);
        } else if (!response) {
            debugLog.error('Failed to process face');
        }
    }

    private async _submitCapture(id: string, capture: BestFaceCapture) {
        // Prevent concurrent submissions for the same subject
        if (this._activeSubmissions.has(id)) return;
        this._activeSubmissions.add(id);

        const age = performance.now() - capture.capturedAt;
        debugLog.info(`Submitting face (quality: ${capture.quality.toFixed(3)}, age: ${age.toFixed(0)}ms)`);
        capture.submitted = true;

        runInAction(() => {
            this.isProcessingFace = true;
            this.processingFaceImage = capture.expandedImage;
        });

        try {
            await this.submissionHandler(capture.expandedImage, id);
        } catch (e) {
            debugLog.error(`Submit error: ${e instanceof Error ? e.message : String(e)}`);
        } finally {
            const captureStillTracked = this.highestQualityFaces.get(id) === capture;
            this._activeSubmissions.delete(id);
            runInAction(() => {
                this.isProcessingFace = false;
                this.processingFaceImage = null;
            });
            if (!captureStillTracked) {
                closeCapture(capture);
            }
        }

        // Schedule cleanup after debounce period to prevent re-submitting the same person
        this._scheduleCleanup(id);
    }

    private _scheduleCleanup(id: string) {
        this._clearTimer(this._cleanupTimers, id);
        const timer = setTimeout(() => {
            this._cleanupTimers.delete(id);
            this._deleteHighestQualityFace(id);
        }, this._settings.faceDebouceDurationMs);
        this._cleanupTimers.set(id, timer);
    }

    // ── API communication ──

    private _getApi(): Api<unknown> {
        const baseUrl = this._settings.identitiesSetup.serverAddress.trim();
        if (!this._api || this._apiBaseUrl !== baseUrl) {
            this._api = new Api({ baseUrl });
            this._apiBaseUrl = baseUrl;
        }
        return this._api;
    }

    private async _publishFace(bitmap: ImageBitmap): Promise<FaceEdgePublishResponse | null> {
        const { identitiesSetup } = this._settings;
        if (!identitiesSetup.isConnected || !identitiesSetup.authToken) {
            debugLog.error('Cannot send face: not connected to identities service');
            return null;
        }

        try {
            const expanded = await bitmapToBase64Jpeg(bitmap);
            const authToken = await identitiesSetup.getLatestToken();

            const payload: FaceEdgeRecognizeWithOptions = {
                croppedAligned: null,
                expanded,
                embeddings: null,
                recognitionModel: null,
                detectTimeUtc: new Date().toISOString(),
                ianaTimeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
                detectorName: this._settings.detectorName || 'eConnect Face SDK',
                largestFaceOnly: true,
                allowDoubleDetection: true,
            };

            debugLog.info(`Submitting face to server (${(expanded.length / 1024).toFixed(1)}KB)`);
            const response = await this._getApi().api.faceEdgePublishWithResponse(payload, {
                headers: { Authorization: `Bearer ${authToken}` },
            });
            debugLog.info('Server response received');
            return response;
        } catch (e) {
            debugLog.error(`Server submission failed: ${e instanceof Error ? e.message : String(e)}`);
            return null;
        }
    }

    // ── Event classification ──

    private async _awaitClassification(detectId: string): Promise<void> {
        debugLog.info(`Awaiting classification (detectId: ${detectId.substring(0, 8)}...)`);
        const events = this._rootStore.identitiesEventsStore.faceDetectionAttributes;
        const value = await awaitClassification(events, detectId, CLASSIFICATION_TIMEOUT_MS);
        if (value !== null) {
            debugLog.info(`Classification received for detectId ${detectId}: ${value}`);
            this.setLastEventClassification(value);
        } else {
            debugLog.warn(`Classification timed out for detectId ${detectId} after (${CLASSIFICATION_TIMEOUT_MS}ms)`);
            this.setLastEventClassification(null);
        }
    }

    private _scheduleClassificationReset() {
        this._clearClassificationTimer();
        const duration = this._settings.eventThemeDisplayDurationMs || 5000;
        this._classificationResetTimer = setTimeout(() => {
            this._classificationResetTimer = null;
            this.resetEventClassification();
        }, duration);
    }

    private _clearClassificationTimer() {
        if (this._classificationResetTimer) {
            clearTimeout(this._classificationResetTimer);
            this._classificationResetTimer = null;
        }
    }

    // ── Timer utilities ──

    private _clearTimer(timers: Map<string, ReturnType<typeof setTimeout>>, id: string) {
        const t = timers.get(id);
        if (t) { clearTimeout(t); timers.delete(id); }
    }

    private _clearAllTimers() {
        for (const t of this._submitTimers.values()) clearTimeout(t);
        for (const t of this._cleanupTimers.values()) clearTimeout(t);
        this._submitTimers.clear();
        this._cleanupTimers.clear();
        this._activeSubmissions.clear();
        this._clearClassificationTimer();
    }

    // ── Disposal ──

    dispose() {
        super.dispose();
        this.stopFaceDetection();
        this._clearHighestQualityFaces();
        this._clearAllTimers();
        this._processTimeAvg.reset();
        closeBitmap(this.processingFaceImage);
        this.processingFaceImage = null;
        this.laplacianByBoxId.clear();
        this._api = null;
    }
}
